import React, { useContext, useEffect, useMemo } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import {
  getDatasourceValue,
  getDateTimeValue,
  isNumber,
} from 'designer/component/action/Data'
import { Badge } from 'react-bootstrap'

const Title = (props) => {
  const designContext = useContext(DesignContext)
  const { isUiChanged, setIsUiChanged, jsonForm } = designContext
  const formData = jsonForm.formData
  const uiChanged = objectPath(isUiChanged)
  let {
    uischema,
    config = {},
    isActive,
    activeTextColor,
    activeColor,
    isModalTitle,
    modalTitle,
  } = props
  let { dynamicName, dynamicIndex } = config
  let { fontSize, fontWeight } = uischema
  let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
  const isRender = uiChanged.get([uischema.key, 'isRender'])
  const viewListItem = uiChanged.get(['viewListItem', uischema.key])
  const { formatDate, formatTime, formatDateTime } = useTimeFormat()
  const dynamicData = objectPath.get(formData, `${dynamicName}.${dynamicIndex}`)

  const formatCurrency = (value) => {
    if (!value) return value
    if (!isNumber(value)) return value
    if (isNumber(value)) {
      value = value.toString()
    }
    const num = parseFloat(value.replace(/[^0-9.]/g, ''))
    return num ? num.toLocaleString() : ''
  }

  const title = useMemo(() => {
    let joinTitle = uischema.joinTitle
    let { titleType, titleValue, title } = uischema

    if (
      isModalTitle &&
      !title &&
      (titleType !== 'value' || !titleValue?.length)
    ) {
      return modalTitle
    }

    if (titleType === 'value' && titleValue) {
      let valueList = []
      titleValue.forEach((item) => {
        let { value, title, type } = item
        if (type === 'badge') return
        if (title) {
          let valueItem = []
          title.forEach((subItem) => {
            let key = subItem.value
            let isNew = objectPath.get(subItem, '__isNew__')
            let titleValue = ''
            if (isNew) {
              if (key === '@numberOrder') {
                titleValue = dynamicIndex + 1
              } else if (key.startsWith('@')) {
                let itemKey = key.replace('@', '')
                if (viewListItem) {
                  let data = viewListItem.item
                  titleValue = objectPath.get(data, itemKey)
                } else if (isModalTitle) {
                  titleValue = objectPath.get(
                    datasourceValue,
                    `receiveProps.${itemKey}`,
                  )
                } else {
                  titleValue = objectPath.get(dynamicData, itemKey)
                }
              } else {
                titleValue = subItem.value
              }
            } else {
              titleValue = getDatasourceValue({
                item,
                key,
                datasourceValue,
                formatDate,
                formatTime,
                formatDateTime,
              })
            }
            if (type === 'currency') {
              if (key.startsWith('@')) {
                titleValue = formatCurrency(titleValue)
              }
            } else if (type === 'dateTime') {
              if (key.startsWith('@')) {
                titleValue = getDateTimeValue(titleValue, item, {
                  formatDate,
                  formatTime,
                  formatDateTime,
                })
              }
            }
            valueItem.push(titleValue)
          })
          valueList.push(valueItem.join(' '))
        } else {
          let titleValue = objectPath.get(datasourceValue, value)
          if (titleValue) {
            valueList.push(titleValue)
          } else {
            valueList.push(value)
          }
        }
      })
      return valueList.join(joinTitle ?? ' ')
    }

    return title
  }, [
    isRender,
    uischema.joinTitle,
    uischema.titleType,
    uischema.titleValue,
    uischema.title,
    JSON.stringify(datasourceValue),
    JSON.stringify(dynamicData),
    JSON.stringify(viewListItem),
  ])

  const badge = useMemo(() => {
    let joinTitle = uischema.joinTitle
    let { titleType, titleValue } = uischema
    let title = ''
    let position = 'left'
    let badgeColor = '#FFF'
    let badgeTextColor = '#000'
    if (titleType === 'value' && titleValue) {
      let valueList = []
      titleValue.forEach((item) => {
        let { value, title, type } = item
        if (type !== 'badge') return
        position = item?.badgePosition ?? position
        badgeColor = item?.badgeColor ?? badgeColor
        badgeTextColor = item?.badgeTextColor ?? badgeTextColor
        if (title) {
          let valueItem = []
          title.forEach((subItem) => {
            let key = subItem.value
            let isNew = objectPath.get(subItem, '__isNew__')
            let titleValue = ''
            if (isNew) {
              if (key === '@numberOrder') {
                titleValue = dynamicIndex + 1
              } else if (key.startsWith('@')) {
                let itemKey = key.replace('@', '')
                if (isModalTitle) {
                  titleValue = objectPath.get(
                    datasourceValue,
                    `receiveProps.${itemKey}`,
                  )
                } else {
                  titleValue = objectPath.get(dynamicData, itemKey)
                }
              } else {
                titleValue = subItem.value
              }
            } else {
              titleValue = getDatasourceValue({
                item,
                key,
                datasourceValue,
                formatDate,
                formatTime,
                formatDateTime,
              })
            }
            if (type === 'currency') {
              if (key.startsWith('@')) {
                titleValue = formatCurrency(titleValue)
              }
            } else if (type === 'dateTime') {
              if (key.startsWith('@')) {
                titleValue = getDateTimeValue(titleValue, item, {
                  formatDate,
                  formatTime,
                  formatDateTime,
                })
              }
            }
            valueItem.push(titleValue)
          })
          valueList.push(valueItem.join(' '))
        } else {
          let titleValue = objectPath.get(datasourceValue, value)
          if (titleValue) {
            valueList.push(titleValue)
          } else {
            valueList.push(value)
          }
        }
      })
      title = valueList.join(joinTitle ?? ' ')
    }
    return { [position]: title, badgeColor, badgeTextColor }
  }, [
    isRender,
    uischema.joinTitle,
    uischema.titleType,
    uischema.titleValue,
    uischema.title,
    JSON.stringify(datasourceValue),
    JSON.stringify(dynamicData),
  ])

  useEffect(() => {
    if (isRender) {
      uiChanged.del(uischema.key)
      setIsUiChanged(uiChanged.get(''))
    }
  }, [isRender])

  return (
    <>
      {badge?.left && (
        <Badge
          className="mr-2"
          style={{
            padding: '3px 5px',
            marginTop: '2px',
            height: '18px',
            fontSize: '12px',
            color: isActive ? activeTextColor : badge.badgeTextColor,
            backgroundColor: isActive ? activeColor : badge.badgeColor,
          }}
        >
          {badge.left}
        </Badge>
      )}
      <span style={{ fontSize: fontSize, fontWeight: fontWeight }}>
        {title}
      </span>
      {badge?.right && (
        <Badge
          className="ml-2"
          style={{
            padding: '3px 5px',
            marginTop: '2px',
            height: '18px',
            fontSize: '12px',
            color: isActive ? activeTextColor : badge.badgeTextColor,
            backgroundColor: isActive ? activeColor : badge.badgeColor,
          }}
        >
          {badge.right}
        </Badge>
      )}
    </>
  )
}

export default Title
